/**
 * NominatimCache — caché compartida de las consultas a Nominatim (NOMFIX-a2).
 *
 * La política de la OSMF exige guardar los resultados y llama «cliente
 * defectuoso» al que repite la misma consulta. Hasta ahora cada instancia de
 * `NominatimGeocoder` tenía su `Map`, Overpass/telecom no tenía ninguna, los
 * «sin resultado» no se guardaban y nada sobrevivía a recargar la pestaña
 * (medida C de `AUDIT_NOMPOL`).
 *
 * DOP selladas por Luis (25-oct-2026):
 *  - DOP-1a: memoria del módulo (la comparten todos los clientes) +
 *    `sessionStorage` de la pestaña. Vive lo que vive el documento, porque las
 *    claves llevan nombres de infraestructuras y municipios del documento:
 *    SALDOC la vacía al perderse la sesión (`vaciarCacheNominatim`).
 *  - DOP-1b: los «sin resultado» caducan a la hora; los aciertos duran lo que
 *    la pestaña. Un fallo de red, un 429 o un tiempo agotado NO es un «sin
 *    resultado» y quien llama no debe guardarlo.
 *
 * Cada cliente usa su propio prefijo de clave (`nom|`, `ovp|`): sus resultados
 * no tienen la misma forma.
 *
 * @module services/geocoding/cache/NominatimCache
 */

/** Clave de `sessionStorage`. La lee SALDOC a través de `vaciarCacheNominatim`. */
export const CLAVE_CACHE_NOMINATIM = 'ptel-nominatim-cache';

/** Un «sin resultado» se vuelve a preguntar pasada 1 hora. */
export const TTL_SIN_RESULTADO_MS = 60 * 60 * 1000;

/** Tope de entradas guardadas (se descartan las más antiguas). */
export const MAX_ENTRADAS_CACHE_NOMINATIM = 500;

interface Entrada {
  /** Instante (ms epoch) en que se guardó. */
  t: number;
  /** El resultado, o `null` si Nominatim contestó «sin resultado». */
  v: unknown | null;
}

export type LecturaCache<T> = { acierto: true; valor: T | null } | { acierto: false };

let memoria: Map<string, Entrada> | null = null;

function almacen(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function esEntrada(e: unknown): e is Entrada {
  return !!e && typeof e === 'object' && typeof (e as Entrada).t === 'number' && 'v' in (e as object);
}

/** Carga perezosa: la primera vez lee lo que dejó esta pestaña antes de recargar. */
function cargar(): Map<string, Entrada> {
  if (memoria) return memoria;
  const mapa = new Map<string, Entrada>();
  memoria = mapa;
  const st = almacen();
  if (!st) return mapa;
  try {
    const crudo = st.getItem(CLAVE_CACHE_NOMINATIM);
    if (!crudo) return mapa;
    const doc = JSON.parse(crudo) as { v?: number; e?: Record<string, unknown> };
    if (!doc || doc.v !== 1 || !doc.e || typeof doc.e !== 'object') return mapa;
    for (const [clave, entrada] of Object.entries(doc.e)) {
      if (esEntrada(entrada)) mapa.set(clave, entrada);
    }
  } catch {
    // JSON corrupto o almacén inaccesible: se empieza vacío.
  }
  return mapa;
}

function persistir(): void {
  const st = almacen();
  if (!st || !memoria) return;
  try {
    st.setItem(CLAVE_CACHE_NOMINATIM, JSON.stringify({ v: 1, e: Object.fromEntries(memoria) }));
  } catch {
    // Cuota llena o almacén bloqueado: se sigue solo en memoria.
  }
}

function guardar(clave: string, valor: unknown | null): void {
  const mapa = cargar();
  mapa.delete(clave); // reinserta al final: el orden de inserción es el de antigüedad
  mapa.set(clave, { t: Date.now(), v: valor });
  while (mapa.size > MAX_ENTRADAS_CACHE_NOMINATIM) {
    const masAntigua = mapa.keys().next().value;
    if (masAntigua === undefined) break;
    mapa.delete(masAntigua);
  }
  persistir();
}

/** Lee una consulta. Un «sin resultado» vencido cuenta como no encontrado. */
export function leerCacheNominatim<T>(clave: string): LecturaCache<T> {
  const mapa = cargar();
  const entrada = mapa.get(clave);
  if (!entrada) return { acierto: false };
  if (entrada.v === null && Date.now() - entrada.t > TTL_SIN_RESULTADO_MS) {
    mapa.delete(clave);
    persistir();
    return { acierto: false };
  }
  return { acierto: true, valor: entrada.v as T | null };
}

/** Guarda un resultado de Nominatim. */
export function guardarAciertoNominatim(clave: string, valor: unknown): void {
  guardar(clave, valor);
}

/** Guarda que Nominatim contestó «sin resultado» (NUNCA un fallo de red, 429 o tiempo agotado). */
export function guardarSinResultadoNominatim(clave: string): void {
  guardar(clave, null);
}

/**
 * Vacía la caché. Sin prefijo borra todo (memoria y `sessionStorage`): es lo
 * que llama SALDOC al perderse la sesión. Con prefijo, solo las claves que
 * empiezan por él (`clearCache()` de cada cliente).
 */
export function vaciarCacheNominatim(prefijo?: string): void {
  if (prefijo === undefined) {
    memoria = new Map();
    const st = almacen();
    try {
      st?.removeItem(CLAVE_CACHE_NOMINATIM);
    } catch {
      // nada que borrar si el almacén no responde
    }
    return;
  }
  const mapa = cargar();
  for (const clave of [...mapa.keys()]) {
    if (clave.startsWith(prefijo)) mapa.delete(clave);
  }
  persistir();
}

/** Número de entradas con el prefijo dado (todas si no se indica). */
export function contarCacheNominatim(prefijo = ''): number {
  return [...cargar().keys()].filter((c) => c.startsWith(prefijo)).length;
}

/** Solo para pruebas: olvida la memoria SIN tocar `sessionStorage` (como una recarga de pestaña). */
export function _simularRecargaDeLaPestanaParaPruebas(): void {
  memoria = null;
}
